/**
 * Pure unit conversion utilities for the Phoenix Tracker.
 * All stored values are in metric (kg, cm). These functions handle
 * conversion between metric and imperial for display and input.
 */

export const KG_TO_LB = 2.20462
export const CM_TO_IN = 0.393701

// Generous upper bound for body weight in kg. The heaviest human ever
// recorded was ~635 kg; 500 is well past any realistic entry while still
// catching typo'd or corrupt rows (e.g. a "42857" weight that blows out
// the chart's y-axis).
export const MAX_PLAUSIBLE_WEIGHT_KG = 500

export function isPlausibleWeightKg(kg) {
  return Number.isFinite(kg) && kg > 0 && kg <= MAX_PLAUSIBLE_WEIGHT_KG
}

export function kgToLb(kg) {
  return kg * KG_TO_LB
}

export function lbToKg(lb) {
  return lb / KG_TO_LB
}

export function cmToIn(cm) {
  return cm * CM_TO_IN
}

export function inToCm(inches) {
  return inches * 2.54
}

/**
 * Convert a stored kg value to the user's preferred system for display.
 * Returns the raw number (no formatting).
 */
export function displayWeight(kg, system) {
  if (kg == null) return null
  return system === 'imperial' ? kgToLb(kg) : kg
}

/**
 * Convert a stored cm value to the user's preferred system for display.
 */
export function displayLength(cm, system) {
  if (cm == null) return null
  return system === 'imperial' ? cmToIn(cm) : cm
}

/**
 * Convert a user-entered weight value (in their selected system) to kg for storage.
 */
export function inputWeightToKg(value, system) {
  if (value == null || value === '') return null
  const num = Number(value)
  if (Number.isNaN(num)) return null
  return system === 'imperial' ? lbToKg(num) : num
}

/**
 * Convert a user-entered length value (in their selected system) to cm for storage.
 */
export function inputLengthToCm(value, system) {
  if (value == null || value === '') return null
  const num = Number(value)
  if (Number.isNaN(num)) return null
  return system === 'imperial' ? inToCm(num) : num
}
